import { useEffect, useRef, useState } from 'react';

import { Alert, Button, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';

import { describeDigestError } from '@/components/RegistryOrders/RegistryDigest/digestErrors';
import type {
  RegistryDigestAddedToReport,
  RegistryDigestAddToReportPreview,
  RegistryReportContent,
} from '@/types/registry-documents';
import { toError } from '@/utils/errors';

/** The first add, an update of the block the report still carries, or
 * adding again once a later version of the report dropped it (the same
 * call as the first add). */
export type RegistryDigestInsertionMode = 'add' | 'update' | 'again';

/** The button's label, which is also the dialog's title. */
export const INSERTION_LABELS: Record<RegistryDigestInsertionMode, string> = {
  add: 'Add to report',
  update: 'Update in report',
  again: 'Add again',
};

/** Where the block goes, from the dry run, as one sentence. Nothing
 * already in the report is altered except the block an earlier add left,
 * which the `replaced_block` sentence says is refreshed in place. */
export function placementSentence(
  preview: Pick<RegistryDigestAddToReportPreview, 'placement'>
): string {
  switch (preview.placement) {
    case 'replaced_block':
      return 'Replaces the block added earlier (edits made inside it are lost).';
    case 'inside_section':
      return "Goes under the report's Registry documents section.";
    case 'before_appendix':
      return 'Goes before the Appendix / Sources.';
    case 'before_footer':
      return 'Goes at the end of the report (before the footer).';
    case 'end':
      return 'Goes at the end of the report.';
    default:
      return 'Goes into the report.';
  }
}

/** What the block is added to: the Word file of a legacy report, the
 * markdown of a Hub-written one, or the report when the kind is unknown. */
function reportNoun(reportContent: Pick<RegistryReportContent, 'kind'>): string {
  switch (reportContent.kind) {
    case 'docx':
      return "the report's Word file";
    case 'markdown':
      return "the report's text";
    default:
      return 'the report';
  }
}

/** The styles the report's template lacks, as one dimmed line, or null. */
export function styleFallbackSentence(styleFallbacks: readonly string[]): string | null {
  if (styleFallbacks.length === 0) {
    return null;
  }
  const names = styleFallbacks.map((name) => `'${name}'`).join(', ');
  return `The report's template has no ${names} ${styleFallbacks.length === 1 ? 'style' : 'styles'}: the sections use the nearest look instead.`;
}

interface PreviewBodyProps {
  preview: RegistryDigestAddToReportPreview;
  companyName: string;
  reportContent: RegistryReportContent;
}

/** What the insertion would do, sentence by sentence. */
function PreviewBody({ preview, companyName, reportContent }: Readonly<PreviewBodyProps>) {
  const sentence = placementSentence(preview);
  const replaces = preview.placement === 'replaced_block';
  const fallbacks = styleFallbackSentence(preview.styleFallbacks);
  const straightAway =
    !reportContent.approvalRequired && reportContent.lifecycleStage === 'completed';
  return (
    <>
      <Text size="sm">
        The document summaries for {companyName} are added to {reportNoun(reportContent)} as a new
        version ({preview.contentVersion + 1}), a section of their own. Nothing already in the
        report is altered, and earlier versions stay available.
      </Text>
      <Text size="sm" data-testid="registry-digest-placement">
        {replaces ? (
          <>
            <Text span inherit fw={700}>
              Replaces
            </Text>
            {sentence.slice('Replaces'.length)}
          </>
        ) : (
          sentence
        )}
      </Text>
      {preview.wouldRevoke && (
        <Alert color="orange" data-testid="registry-digest-revoke-warning">
          This report is approved and visible to the client. Adding the sections withdraws that
          approval: the report goes back to QA and the client cannot see it until it is approved
          again.
        </Alert>
      )}
      {straightAway && (
        <Alert color="blue" data-testid="registry-digest-no-approval">
          This report needs no approval: the client sees the new version straight away.
        </Alert>
      )}
      {fallbacks && (
        <Text size="xs" c="dimmed" data-testid="registry-digest-style-fallbacks">
          {fallbacks}
        </Text>
      )}
      <Text size="sm" c="dimmed">
        Nothing is sent to the client.
      </Text>
    </>
  );
}

/** What the dialog knows of the dry run. */
type PreviewState =
  | { kind: 'loading' }
  | { kind: 'ready'; preview: RegistryDigestAddToReportPreview }
  | { kind: 'refused'; thrown: Error | null };

interface RegistryDigestAddToReportModalProps {
  opened: boolean;
  onClose: () => void;
  companyName: string;
  mode: RegistryDigestInsertionMode;
  /** The report's document as the order's detail describes it: whether
   * its client sees a new version at once. */
  reportContent: RegistryReportContent;
  /** The dry run, asked for when the dialog opens. Null, or a throw, when
   * refused. */
  onPreview: () => Promise<RegistryDigestAddToReportPreview | null>;
  /** The write, from the version the dry run built from. Null, or a
   * throw, when refused; the dialog stays open and says why. */
  onConfirm: (expectedContentVersion: number) => Promise<RegistryDigestAddedToReport | null>;
  /** Why the last preview or write was refused, when it answered null
   * rather than throwing (the hook keeps the error). */
  error: Error | null;
  /** The write succeeded: the dialog has closed and said so. */
  onAdded?: (result: RegistryDigestAddedToReport) => void;
}

/**
 * Confirm appending the digest to the report, a Word file or Hub-written
 * markdown alike. The dialog asks the API for a dry run as it opens and
 * shows what the insertion would do before anything is written: where
 * the block goes (nothing already in the report is altered, except the
 * block an earlier add left, refreshed in place) and whether the
 * report's approval is withdrawn. The confirm names the version the dry
 * run built from, so a report that moved on meanwhile is refused rather
 * than overwritten; a report being edited through feedback is refused
 * as busy.
 */
export function RegistryDigestAddToReportModal({
  opened,
  onClose,
  companyName,
  mode,
  reportContent,
  onPreview,
  onConfirm,
  error,
  onAdded,
}: Readonly<RegistryDigestAddToReportModalProps>) {
  const [state, setState] = useState<PreviewState>({ kind: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  // Why the last write failed: the thrown error, or null for one the
  // caller answered with null (then `error` says why).
  const [writeFailure, setWriteFailure] = useState<Error | null | undefined>(undefined);
  // The latest callbacks, so the dry run runs once per opening and never
  // again because a parent re-rendered with new closures.
  const previewRef = useRef(onPreview);
  previewRef.current = onPreview;
  // Which opening a dry run belongs to: an answer that lands after the
  // dialog closed, or reopened, is dropped.
  const openingRef = useRef(0);

  useEffect(() => {
    if (!opened) {
      return;
    }
    const opening = ++openingRef.current;
    setState({ kind: 'loading' });
    setWriteFailure(undefined);
    const settle = (next: PreviewState) => {
      if (openingRef.current === opening) {
        setState(next);
      }
    };
    previewRef
      .current()
      .then((preview) =>
        settle(preview ? { kind: 'ready', preview } : { kind: 'refused', thrown: null })
      )
      .catch((err: unknown) => settle({ kind: 'refused', thrown: toError(err) }));
    return () => {
      openingRef.current += 1;
    };
  }, [opened]);

  const label = INSERTION_LABELS[mode];

  const submit = async () => {
    if (state.kind !== 'ready') {
      return;
    }
    setSubmitting(true);
    setWriteFailure(undefined);
    try {
      const result = await onConfirm(state.preview.contentVersion);
      if (!result) {
        setWriteFailure(null);
        return;
      }
      notifications.show({
        title: `${mode === 'update' ? 'Updated in report' : 'Added to report'} as version ${result.contentVersion}`,
        message: result.approvalRevoked
          ? 'Approval withdrawn: the report is back in QA.'
          : 'Earlier versions stay available.',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      onClose();
      onAdded?.(result);
    } catch (err) {
      setWriteFailure(toError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // A write in flight is not abandoned by dismissing the dialog: its
  // outcome, a refusal above all, must still have somewhere to be shown.
  const dismiss = () => {
    if (!submitting) {
      onClose();
    }
  };

  const fallback = new Error('The request failed');
  const refusalWords = (thrown: Error | null) => describeDigestError(thrown ?? error ?? fallback);

  return (
    <Modal opened={opened} onClose={dismiss} title={label}>
      <Stack>
        {state.kind === 'loading' && (
          <Group gap="xs" role="status">
            <Loader size="xs" />
            <Text size="sm" c="dimmed">
              Checking where the sections go…
            </Text>
          </Group>
        )}
        {state.kind === 'refused' && (
          <Alert
            color="red"
            title="The sections cannot be added"
            data-testid="registry-digest-preview-refused"
          >
            {refusalWords(state.thrown)}
          </Alert>
        )}
        {state.kind === 'ready' && (
          <PreviewBody
            preview={state.preview}
            companyName={companyName}
            reportContent={reportContent}
          />
        )}
        {writeFailure !== undefined && (
          <Alert
            color="red"
            title="The report was not changed"
            data-testid="registry-digest-add-failed"
          >
            {refusalWords(writeFailure)}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={dismiss} disabled={submitting}>
            Keep as is
          </Button>
          {state.kind === 'ready' && (
            <Button
              onClick={() => void submit()}
              loading={submitting}
              data-testid="registry-digest-add-to-report-confirm"
            >
              {label}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
